import React, { useState, useEffect } from 'react';
import { Save, X, User, Mail, Phone, FileText, MapPin, Calendar, Building, Users, Truck, RefreshCw, CheckCircle, AlertCircle, Key, Eye, EyeOff, Shield, UserCheck } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import CepAutoFill from './CepAutoFill';
import { useAuth } from '../../contexts/AuthContext';
import { asaasHelpers } from '../../lib/asaasHelpers';
import toast from 'react-hot-toast';

const PersonForm = ({ onSubmit, onCancel, initialData = null, loading = false }) => {
  const { company } = useAuth();
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    document: initialData?.document || '',
    type: initialData?.type || 'customer',
    birth_date: initialData?.birth_date || '',
    
    // Campos específicos para integração Asaas (apenas para responsáveis e funcionários)
    person_type: (initialData?.type === 'parent' || initialData?.type === 'employee') ? (initialData?.person_type || 'FISICA') : 'FISICA',
    state_registration: initialData?.state_registration || '',
    municipal_registration: initialData?.municipal_registration || '',
    observations: initialData?.observations || '',
    
    // Endereço
    address: initialData?.address || '',
    address_number: initialData?.address_number || '',
    address_complement: initialData?.address_complement || '',
    neighborhood: initialData?.neighborhood || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    zip_code: initialData?.zip_code || '',
    postal_code: initialData?.postal_code || initialData?.zip_code || '',
    city_code: initialData?.city_code || '',
    country: initialData?.country || 'Brasil',
    
    // Configurações
    active: initialData?.active !== undefined ? initialData.active : true,
    notes: initialData?.notes || '',
    
    // Asaas sync fields (readonly)
    asaas_customer_id: initialData?.asaas_customer_id || '',
    asaas_sync_status: initialData?.asaas_sync_status || 'pending',
    
    
    // Controle de acesso ao sistema (opcional)
    create_user_access: initialData?.auth_user_id ? true : false,
    user_password: '',
    user_confirm_password: '',
    user_type: initialData?.user_type || 'collaborator',
    user_permissions: {
      pdv: initialData?.user_permissions?.pdv !== undefined ? initialData.user_permissions.pdv : true,
      students: initialData?.user_permissions?.students !== undefined ? initialData.user_permissions.students : false,
      products: initialData?.user_permissions?.products !== undefined ? initialData.user_permissions.products : false,
      reports: initialData?.user_permissions?.reports !== undefined ? initialData.user_permissions.reports : false,
      settings: initialData?.user_permissions?.settings !== undefined ? initialData.user_permissions.settings : false,
      financial: initialData?.user_permissions?.financial !== undefined ? initialData.user_permissions.financial : false
    }
  });

  const [errors, setErrors] = useState({});
  const [syncingAsaas, setSyncingAsaas] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Resetar campos Asaas quando mudar o tipo da pessoa
  useEffect(() => {
    if (formData.type !== 'parent' && formData.type !== 'employee') {
      setFormData(prev => ({
        ...prev,
        person_type: 'FISICA',
        state_registration: '',
        municipal_registration: '',
        observations: '',
        city_code: '',
        country: 'Brasil'
      }));
    }
  }, [formData.type]);

  const personTypes = [
    { value: 'customer', label: 'Cliente', icon: Users, description: 'Pessoa física ou jurídica que compra produtos/serviços' },
    { value: 'supplier', label: 'Fornecedor', icon: Truck, description: 'Empresa ou pessoa que fornece produtos/serviços' },
    { value: 'user', label: 'Usuário', icon: User, description: 'Pessoa com acesso ao sistema' },
    { value: 'student', label: 'Aluno', icon: User, description: 'Estudante da instituição' },
    { value: 'parent', label: 'Responsável', icon: Users, description: 'Responsável por aluno (cliente Asaas)' },
    { value: 'employee', label: 'Funcionário', icon: Building, description: 'Professor, secretário ou funcionário da escola (cliente Asaas)' }
  ];

  const userTypes = [
    { value: 'admin', label: 'Administrador', description: 'Acesso total ao sistema' },
    { value: 'collaborator', label: 'Colaborador', description: 'Acesso limitado conforme permissões' },
    { value: 'parent', label: 'Portal dos Pais', description: 'Acesso ao portal para acompanhar alunos' }
  ];

  const permissionLabels = {
    pdv: { label: 'PDV (Ponto de Venda)', description: 'Realizar vendas e operações do caixa' },
    students: { label: 'Gestão de Alunos', description: 'Cadastrar e gerenciar alunos' },
    products: { label: 'Gestão de Produtos', description: 'Cadastrar e gerenciar produtos' },
    reports: { label: 'Relatórios', description: 'Visualizar relatórios e estatísticas' },
    financial: { label: 'Financeiro', description: 'Gerenciar saldos, recargas e cobranças' },
    settings: { label: 'Configurações', description: 'Alterar configurações do sistema' }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionChange = (permission, value) => {
    setFormData(prev => ({
      ...prev,
      user_permissions: {
        ...prev.user_permissions,
        [permission]: value
      }
    }));
  };

  const handleUserTypeChange = (newType) => {
    setFormData(prev => {
      let newPermissions = { ...prev.user_permissions };
      
      // Configurar permissões padrão baseado no tipo
      if (newType === 'admin') {
        newPermissions = {
          pdv: true,
          students: true,
          products: true,
          reports: true,
          financial: true,
          settings: true
        };
      } else if (newType === 'collaborator') {
        newPermissions = {
          pdv: true,
          students: false,
          products: false,
          reports: false,
          financial: false,
          settings: false
        };
      } else if (newType === 'parent') {
        newPermissions = {
          pdv: false,
          students: false,
          products: false,
          reports: false,
          financial: false,
          settings: false
        };
      }

      return {
        ...prev,
        user_type: newType,
        user_permissions: newPermissions
      };
    });
  };

  const generatePassword = () => {
    const password = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-2).toUpperCase() + '123';
    setFormData(prev => ({
      ...prev,
      user_password: password,
      user_confirm_password: password
    }));
    toast.success('Senha gerada automaticamente');
  };

  const getPermissionCount = () => {
    return Object.values(formData.user_permissions).filter(Boolean).length;
  };

  const handleCepAddress = (cepData) => {
    setFormData(prev => ({
      ...prev,
      address: cepData.street || prev.address,
      neighborhood: cepData.neighborhood || prev.neighborhood,
      city: cepData.city || prev.city,
      state: cepData.state || prev.state,
      zip_code: cepData.cep || prev.zip_code
    }));
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value.slice(0, 15);
  };

  const formatDocument = (value, type) => {
    const numbers = value.replace(/\D/g, '');
    
    if (type === 'supplier' && numbers.length <= 14) {
      // CNPJ
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    } else if (numbers.length <= 11) {
      // CPF
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    
    return value;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.type) {
      newErrors.type = 'Tipo de pessoa é obrigatório';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (formData.phone && formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Telefone inválido';
    }

    if (formData.document) {
      const numbers = formData.document.replace(/\D/g, '');
      if ((formData.type === 'supplier' || formData.person_type === 'JURIDICA') && numbers.length !== 14) {
        newErrors.document = 'CNPJ deve ter 14 dígitos';
      } else if ((formData.type !== 'supplier' && formData.person_type === 'FISICA') && numbers.length !== 11) {
        newErrors.document = 'CPF deve ter 11 dígitos';
      }
    }

    // Validações específicas para responsáveis e funcionários (clientes Asaas)
    if (formData.type === 'parent' || formData.type === 'employee') {
      const typeLabel = formData.type === 'parent' ? 'responsáveis' : 'funcionários';
      
      if (!formData.document) {
        newErrors.document = `Documento é obrigatório para ${typeLabel}`;
      }
      
      if (!formData.email) {
        newErrors.email = `E-mail é obrigatório para ${typeLabel}`;
      }
      
      if (formData.person_type === 'FISICA' && !formData.birth_date) {
        newErrors.birth_date = 'Data de nascimento é obrigatória para pessoa física';
      }
      
      if (!formData.phone) {
        newErrors.phone = `Telefone é obrigatório para ${typeLabel}`;
      }
    }

    // Validações específicas para criação de usuário
    if (formData.create_user_access) {
      if (!initialData) { // Apenas para novos cadastros
        if (!formData.user_password) {
          newErrors.user_password = 'Senha é obrigatória para acesso ao sistema';
        } else if (formData.user_password.length < 6) {
          newErrors.user_password = 'Senha deve ter pelo menos 6 caracteres';
        }

        if (formData.user_password !== formData.user_confirm_password) {
          newErrors.user_confirm_password = 'Senhas não coincidem';
        }
      }

      if (!formData.user_type) {
        newErrors.user_type = 'Tipo de usuário é obrigatório';
      }
    }

    if (formData.zip_code && formData.zip_code.replace(/\D/g, '').length !== 8) {
      newErrors.zip_code = 'CEP inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    // Separar os campos do formData para evitar conflitos
    const {
      person_type: _person_type,
      state_registration: _state_registration,
      municipal_registration: _municipal_registration,
      observations: _observations,
      city_code: _city_code,
      country: _country,
      asaas_customer_id: _asaas_customer_id,
      asaas_sync_status: _asaas_sync_status,
      user_confirm_password: _user_confirm_password,
      ...baseFormData
    } = formData;

    const cleanedData = {
      ...baseFormData,
      phone: formData.phone?.replace(/\D/g, '') || null,
      document: formData.document?.replace(/\D/g, '') || null,
      zip_code: formData.zip_code?.replace(/\D/g, '') || null,
      postal_code: formData.postal_code?.replace(/\D/g, '') || formData.zip_code?.replace(/\D/g, '') || null,
      name: formData.name.trim(),
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      address_number: formData.address_number?.trim() || null,
      address_complement: formData.address_complement?.trim() || null,
      neighborhood: formData.neighborhood?.trim() || null,
      city: formData.city?.trim() || null,
      state: formData.state?.trim() || null,
      notes: formData.notes?.trim() || null,
      
      // Campos específicos do Asaas (apenas para responsáveis e funcionários)
      ...(formData.type === 'parent' || formData.type === 'employee' ? {
        person_type: formData.person_type || 'FISICA',
        state_registration: formData.state_registration?.trim() || null,
        municipal_registration: formData.municipal_registration?.trim() || null,
        observations: formData.observations?.trim() || null,
        city_code: formData.city_code?.trim() || null,
        country: formData.country?.trim() || 'Brasil'
      } : {
        // Para outros tipos, não incluir campos Asaas
        person_type: null,
        state_registration: null,
        municipal_registration: null,
        observations: null,
        city_code: null,
        country: null
      }),

      // Dados de usuário (apenas se solicitado acesso)
      ...(formData.create_user_access ? {
        create_user_access: true,
        user_password: formData.user_password || null,
        user_type: formData.user_type,
        user_permissions: formData.user_permissions
      } : {
        create_user_access: false,
        user_password: null,
        user_type: null,
        user_permissions: null
      })
    };

    onSubmit(cleanedData);
  };

  const handleSyncAsaas = async () => {
    if (!initialData?.id || !company?.id) {
      toast.error('ID da pessoa ou empresa não encontrado');
      return;
    }

    try {
      setSyncingAsaas(true);
      
      const syncResult = await asaasHelpers.syncCustomer(initialData.id, company.id, true);
      
      if (syncResult.success) {
        if (syncResult.already_synced) {
          toast.success('Cliente atualizado no Asaas com sucesso!');
        } else {
          toast.success('Cliente criado no Asaas com sucesso!');
        }
        
        // Atualizar os dados do formulário com as informações de sincronização
        setFormData(prev => ({
          ...prev,
          asaas_customer_id: syncResult.customer_id,
          asaas_sync_status: 'synced'
        }));
      } else {
        toast.error(`Erro na sincronização: ${syncResult.error}`);
      }
    } catch (error) {
      console.error('Erro ao sincronizar com Asaas:', error);
      toast.error('Erro ao sincronizar com Asaas');
    } finally {
      setSyncingAsaas(false);
    }
  };

  const selectedType = personTypes.find(t => t.value === formData.type);

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between border-b border-gray-200 pb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData ? 'Editar Pessoa' : 'Cadastrar Pessoa'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {initialData ? 'Atualize as informações da pessoa' : 'Preencha os dados da nova pessoa'}
              </p>
            </div>
          </div>

          {/* Tipo de Pessoa */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tipo de Pessoa</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {personTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <label
                    key={type.value}
                    className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                      formData.type === type.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={type.value}
                      checked={formData.type === type.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center">
                        <div className="text-sm">
                          <div className="flex items-center">
                            <Icon className={`w-5 h-5 mr-2 ${
                              formData.type === type.value ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <p className={`font-medium ${
                              formData.type === type.value ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              {type.label}
                            </p>
                          </div>
                          <p className={`${
                            formData.type === type.value ? 'text-blue-700' : 'text-gray-500'
                          } text-xs mt-1`}>
                            {type.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type}</p>
            )}
          </div>

          {/* Dados Principais */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dados Principais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Nome Completo"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={errors.name}
                  icon={User}
                  placeholder="Nome completo da pessoa"
                  required
                />
              </div>
              
              <Input
                label="E-mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                icon={Mail}
                placeholder="email@exemplo.com"
                required={formData.type === 'parent' || formData.type === 'employee'}
              />
              
              <Input
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
                error={errors.phone}
                icon={Phone}
                placeholder="(11) 99999-9999"
                required={formData.type === 'parent' || formData.type === 'employee'}
              />
              
              <Input
                label={
                  formData.type === 'supplier' || formData.person_type === 'JURIDICA' 
                    ? 'CNPJ' 
                    : 'CPF'
                }
                name="document"
                value={formData.document}
                onChange={(e) => setFormData(prev => ({ ...prev, document: formatDocument(e.target.value, formData.type === 'supplier' || formData.person_type === 'JURIDICA' ? 'supplier' : 'other') }))}
                error={errors.document}
                icon={FileText}
                placeholder={
                  formData.type === 'supplier' || formData.person_type === 'JURIDICA' 
                    ? '00.000.000/0000-00' 
                    : '000.000.000-00'
                }
                required={formData.type === 'parent' || formData.type === 'employee'}
              />
              
              <Input
                label="Data de Nascimento"
                name="birth_date"
                type="date"
                value={formData.birth_date}
                onChange={handleChange}
                error={errors.birth_date}
                icon={Calendar}
                required={(formData.type === 'parent' || formData.type === 'employee') && formData.person_type === 'FISICA'}
              />
            </div>
          </div>

          {/* Campos específicos para Responsáveis e Funcionários (Asaas) */}
          {(formData.type === 'parent' || formData.type === 'employee') && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Informações do Cliente (Asaas)
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Atenção:</strong> {formData.type === 'parent' ? 'Responsáveis' : 'Funcionários'} são automaticamente criados como clientes no sistema Asaas para pagamentos{formData.type === 'employee' ? ' e recarga de conta corrente' : ''}.
                  Preencha todas as informações obrigatórias.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Pessoa
                  </label>
                  <select
                    name="person_type"
                    value={formData.person_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="FISICA">Pessoa Física</option>
                    <option value="JURIDICA">Pessoa Jurídica</option>
                  </select>
                </div>

                {formData.person_type === 'JURIDICA' && (
                  <>
                    <Input
                      label="Inscrição Estadual"
                      name="state_registration"
                      value={formData.state_registration}
                      onChange={handleChange}
                      placeholder="12.345.678-9"
                    />
                    
                    <Input
                      label="Inscrição Municipal"
                      name="municipal_registration"
                      value={formData.municipal_registration}
                      onChange={handleChange}
                      placeholder="123456789"
                    />
                  </>
                )}

                <Input
                  label="Código da Cidade (IBGE)"
                  name="city_code"
                  value={formData.city_code}
                  onChange={handleChange}
                  placeholder="3550308"
                />

                <Input
                  label="País"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Brasil"
                />

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    name="observations"
                    value={formData.observations}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Observações sobre o cliente..."
                  />
                </div>

                {/* Status de sincronização e controles (apenas para edição) */}
                {initialData && (
                  <div className="md:col-span-2">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          Status da Integração Asaas
                        </h4>
                        <Button
                          type="button"
                          onClick={handleSyncAsaas}
                          disabled={syncingAsaas}
                          variant="outline"
                          size="sm"
                          className="flex items-center"
                        >
                          {syncingAsaas ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3 h-3 mr-1" />
                          )}
                          {syncingAsaas ? 'Sincronizando...' : 'Sincronizar'}
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">ID no Asaas:</span>
                          <span className="ml-2 font-mono">
                            {formData.asaas_customer_id || 'Não sincronizado'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Status:</span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs inline-flex items-center ${
                            formData.asaas_sync_status === 'synced' 
                              ? 'bg-green-100 text-green-800' 
                              : formData.asaas_sync_status === 'error'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {formData.asaas_sync_status === 'synced' ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Sincronizado
                              </>
                            ) : formData.asaas_sync_status === 'error' ? (
                              <>
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Erro
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Pendente
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      
                      {formData.asaas_sync_status === 'error' && formData.asaas_sync_error && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          <strong>Erro:</strong> {formData.asaas_sync_error}
                        </div>
                      )}
                      
                      {!formData.asaas_customer_id && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                          <strong>Dica:</strong> Clique em "Sincronizar" para criar este responsável como cliente no Asaas.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Controle de Acesso ao Sistema */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <UserCheck className="w-5 h-5 mr-2 text-blue-600" />
              Acesso ao Sistema (Opcional)
            </h3>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="create_user_access"
                  checked={formData.create_user_access}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    Dar acesso ao sistema (criar login)
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    Marque esta opção para permitir que esta pessoa faça login no sistema
                  </p>
                </div>
              </label>
            </div>

            {formData.create_user_access && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Importante:</strong> Esta pessoa receberá credenciais de acesso ao sistema. 
                    Configure o tipo de usuário e permissões adequadamente.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Usuário *
                    </label>
                    <select
                      name="user_type"
                      value={formData.user_type}
                      onChange={(e) => handleUserTypeChange(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        errors.user_type ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required={formData.create_user_access}
                    >
                      {userTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label} - {type.description}
                        </option>
                      ))}
                    </select>
                    {errors.user_type && (
                      <p className="mt-1 text-sm text-red-600">{errors.user_type}</p>
                    )}
                  </div>

                  {!initialData && (
                    <>
                      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                          <Input
                            label="Senha *"
                            name="user_password"
                            type={showPassword ? "text" : "password"}
                            value={formData.user_password}
                            onChange={handleChange}
                            error={errors.user_password}
                            placeholder="Mínimo 6 caracteres"
                            icon={Key}
                            required={formData.create_user_access}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>

                        <div className="relative">
                          <Input
                            label="Confirmar Senha *"
                            name="user_confirm_password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.user_confirm_password}
                            onChange={handleChange}
                            error={errors.user_confirm_password}
                            placeholder="Repita a senha"
                            icon={Key}
                            required={formData.create_user_access}
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generatePassword}
                          className="text-sm"
                        >
                          <Key className="w-4 h-4 mr-2" />
                          Gerar Senha Automática
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Permissões */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900 flex items-center">
                      <Shield className="w-4 h-4 mr-2" />
                      Permissões do Sistema
                    </h4>
                    <span className="text-sm text-gray-500">
                      {getPermissionCount()} de {Object.keys(permissionLabels).length} permissões ativas
                    </span>
                  </div>
                  
                  {formData.user_type === 'admin' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center">
                        <Shield className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-green-800 font-medium text-sm">
                          Administradores têm acesso total a todas as funcionalidades
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(permissionLabels).map(([key, permission]) => (
                      <div key={key} className={`p-3 border rounded-lg text-sm ${
                        formData.user_type === 'admin' ? 'bg-gray-50 border-gray-200' : 
                        formData.user_permissions[key] ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="font-medium text-gray-900 text-sm">
                              {permission.label}
                            </label>
                            <p className="text-xs text-gray-600 mt-1">
                              {permission.description}
                            </p>
                          </div>
                          <div className="ml-3">
                            <input
                              type="checkbox"
                              checked={formData.user_type === 'admin' || formData.user_permissions[key]}
                              onChange={(e) => handlePermissionChange(key, e.target.checked)}
                              disabled={formData.user_type === 'admin'}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resumo de acesso */}
                {formData.user_password && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2 text-sm">Resumo do Acesso</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-blue-700">Email:</span>
                        <p className="font-medium text-blue-900">{formData.email}</p>
                      </div>
                      <div>
                        <span className="text-blue-700">Tipo:</span>
                        <p className="font-medium text-blue-900">
                          {userTypes.find(t => t.value === formData.user_type)?.label}
                        </p>
                      </div>
                      <div>
                        <span className="text-blue-700">Permissões:</span>
                        <p className="font-medium text-blue-900">
                          {formData.user_type === 'admin' ? 'Todas' : `${getPermissionCount()} ativas`}
                        </p>
                      </div>
                      <div>
                        <span className="text-blue-700">Senha:</span>
                        <p className="font-medium text-blue-900">Configurada</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Endereço */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Endereço</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CepAutoFill
                label="CEP"
                value={formData.zip_code}
                onAddress={handleCepAddress}
                onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                error={errors.zip_code}
              />
              
              <div className="md:col-span-2">
                <Input
                  label="Endereço"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  icon={MapPin}
                  placeholder="Rua, Avenida, etc."
                />
              </div>
              
              <Input
                label="Número"
                name="address_number"
                value={formData.address_number}
                onChange={handleChange}
                placeholder="123"
              />
              
              <Input
                label="Complemento"
                name="address_complement"
                value={formData.address_complement}
                onChange={handleChange}
                placeholder="Apto, Sala, etc."
              />
              
              <Input
                label="Bairro"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                placeholder="Nome do bairro"
              />
              
              <Input
                label="Cidade"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Nome da cidade"
              />
              
              <Input
                label="Estado"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="SP"
                maxLength={2}
              />
            </div>
          </div>

          {/* Configurações */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações</h3>
            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Pessoa ativa
                </span>
              </label>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Observações sobre a pessoa..."
                />
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : initialData ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </div>

          {/* Alertas */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div>
                  <h4 className="font-medium text-red-800">
                    Corrija os seguintes erros:
                  </h4>
                  <ul className="text-sm text-red-700 mt-1">
                    {Object.values(errors).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default PersonForm;